import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Link, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Tabs, TextField, Typography,
} from '@mui/material'
import { Instagram as InstagramIcon } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import PageHeader from '../components/ui/PageHeader'
import { usePermissions } from '../hooks/usePermissions'
import { InfluencerRequest, InfluencerStatus, promotionService } from '../services/promotionService'
import { getErrorMessage } from '../utils/errorHandlers'

const STATUS_LABEL: Record<InfluencerStatus, string> = {
  pending: 'Pending approval', approved: 'Approved – to dispatch', rejected: 'Rejected', dispatched: 'Dispatched – awaiting review', review_received: 'Review received',
}
const STATUS_COLOR: Record<InfluencerStatus, 'warning' | 'info' | 'default' | 'primary' | 'success'> = {
  pending: 'warning', approved: 'info', rejected: 'default', dispatched: 'primary', review_received: 'success',
}
const TABS: { value: string; label: string }[] = [
  { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'To dispatch' }, { value: 'dispatched', label: 'Awaiting review' },
  { value: 'review_received', label: 'Reviewed' }, { value: 'rejected', label: 'Rejected' }, { value: '', label: 'All' },
]
const date = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-')

type ActionKind = 'approve' | 'reject' | 'dispatch' | 'review_received'

const InfluencerRequestsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const canWrite = hasPermission('influencer.write')

  const [status, setStatus] = useState('pending')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [rows, setRows] = useState<InfluencerRequest[]>([])
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)

  const [selected, setSelected] = useState<InfluencerRequest | null>(null)
  const [action, setAction] = useState<ActionKind | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await promotionService.listInfluencerRequests({ status: status || undefined, search: search || undefined, page: page + 1, page_size: pageSize })
      setRows(res.items); setTotal(res.total); setCounts(res.status_counts || {})
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load requests'), { variant: 'error' })
    } finally { setLoading(false) }
  }, [status, search, page, pageSize, enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const begin = (kind: ActionKind) => {
    setAction(kind); setError('')
    setFields(kind === 'approve' ? { approved_products: selected?.requested_products || '' } : {})
  }

  const submit = async () => {
    if (!selected || !action) return
    setSaving(true); setError('')
    try {
      const updated = await promotionService.influencerAction(selected.id, { action, ...fields })
      enqueueSnackbar('Updated', { variant: 'success' })
      setSelected(updated); setAction(null); load()
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update the request'))
    } finally { setSaving(false) }
  }

  return (
    <Box>
      <PageHeader
        title="Influencer Trial Requests"
        subtitle="Instagram creators asking for trial products. Nothing is sent until you approve, and you record their review once it is posted."
        breadcrumbs={[{ label: 'Promotions' }, { label: 'Influencer Requests' }]}
      />
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Tabs value={status} onChange={(_, v) => { setStatus(v); setPage(0) }} variant="scrollable" scrollButtons="auto">
              {TABS.map((t) => <Tab key={t.label} value={t.value} label={t.value ? `${t.label} (${counts[t.value] || 0})` : t.label} />)}
            </Tabs>
            <TextField size="small" placeholder="Search name, @handle, phone or INF- code" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0) }} sx={{ minWidth: 280 }} />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Creator</TableCell><TableCell>Followers</TableCell><TableCell>Wants</TableCell><TableCell>City</TableCell><TableCell>Requested</TableCell><TableCell>Status</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={22} /></TableCell></TableRow>}
                {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>No requests here.</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => { setSelected(r); setAction(null) }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary">@{r.instagram_handle} · {r.reference_code}</Typography>
                    </TableCell>
                    <TableCell>{r.followers_count != null ? r.followers_count.toLocaleString('en-IN') : '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}><Typography variant="body2" noWrap>{r.requested_products || '-'}</Typography></TableCell>
                    <TableCell>{r.city}, {r.state}</TableCell>
                    <TableCell>{date(r.created_at)}</TableCell>
                    <TableCell><Chip size="small" label={STATUS_LABEL[r.status]} color={STATUS_COLOR[r.status]} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[10, 25, 50]} />
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onClose={() => !saving && setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <InstagramIcon fontSize="small" />
                <span>{selected.name}</span>
                <Chip size="small" label={STATUS_LABEL[selected.status]} color={STATUS_COLOR[selected.status]} />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Instagram</Typography><br /><Link href={selected.profile_url || `https://www.instagram.com/${selected.instagram_handle}/`} target="_blank" rel="noopener noreferrer">@{selected.instagram_handle}</Link></Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Followers</Typography><br />{selected.followers_count?.toLocaleString('en-IN') ?? '-'}</Grid>
                <Grid item xs={6} sm={3}><Typography variant="caption" color="text.secondary">Reference</Typography><br />{selected.reference_code}</Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><br />{selected.phone}</Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography><br />{selected.email || '-'}</Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Ship to</Typography><br />{selected.address_line}, {selected.city}, {selected.state} – {selected.pincode}</Grid>
                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Wants to try</Typography><br />{selected.requested_products || '-'}</Grid>
                {selected.message && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Message</Typography><br />{selected.message}</Grid>}
              </Grid>

              {(selected.approved_products || selected.tracking_number || selected.review_url || selected.rejection_reason) && <Divider sx={{ my: 2 }} />}
              {selected.approved_products && <Typography variant="body2">Approved: <b>{selected.approved_products}</b> ({date(selected.reviewed_at)})</Typography>}
              {selected.tracking_number && <Typography variant="body2">Dispatched {date(selected.dispatched_at)} via {selected.courier}, tracking {selected.tracking_number}</Typography>}
              {selected.review_url && <Typography variant="body2">Review: <Link href={selected.review_url} target="_blank" rel="noopener noreferrer">{selected.review_url}</Link> ({date(selected.review_received_at)})</Typography>}
              {selected.rejection_reason && <Alert severity="warning" sx={{ mt: 1 }}>Rejected: {selected.rejection_reason}</Alert>}

              {action && canWrite && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                  {action === 'approve' && <TextField fullWidth multiline minRows={2} label="Products you will send" value={fields.approved_products || ''} onChange={(e) => setFields({ ...fields, approved_products: e.target.value })} />}
                  {action === 'reject' && <TextField fullWidth multiline minRows={2} label="Reason for rejecting" value={fields.rejection_reason || ''} onChange={(e) => setFields({ ...fields, rejection_reason: e.target.value })} />}
                  {action === 'dispatch' && (
                    <Stack direction="row" spacing={1.5}>
                      <TextField fullWidth label="Courier" value={fields.courier || ''} onChange={(e) => setFields({ ...fields, courier: e.target.value })} />
                      <TextField fullWidth label="Tracking number" value={fields.tracking_number || ''} onChange={(e) => setFields({ ...fields, tracking_number: e.target.value })} />
                    </Stack>
                  )}
                  {action === 'review_received' && <TextField fullWidth label="Link to their review post / reel" placeholder="https://www.instagram.com/reel/…" value={fields.review_url || ''} onChange={(e) => setFields({ ...fields, review_url: e.target.value })} />}
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <Button onClick={() => setAction(null)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={submit} disabled={saving} color={action === 'reject' ? 'error' : 'primary'}>{saving ? 'Saving…' : 'Confirm'}</Button>
                  </Stack>
                </Paper>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button onClick={() => setSelected(null)}>Close</Button>
              {canWrite && !action && (
                <Stack direction="row" spacing={1}>
                  {selected.status === 'pending' && <><Button color="error" onClick={() => begin('reject')}>Reject</Button><Button variant="contained" onClick={() => begin('approve')}>Approve</Button></>}
                  {selected.status === 'approved' && <><Button color="error" onClick={() => begin('reject')}>Reject</Button><Button variant="contained" onClick={() => begin('dispatch')}>Mark dispatched</Button></>}
                  {selected.status === 'dispatched' && <Button variant="contained" onClick={() => begin('review_received')}>Record review</Button>}
                </Stack>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default InfluencerRequestsPage
